import { getCookieValue } from '@libs/common-next';
import { DiscountCodes, FulfillmentGroupsItemUI, ItemUI, PaymentMethod } from '@libs/common-node';
import { DeliveryType, PaymentMethodsType } from '@libs/core';

import { dataLayerPush } from '../gtm/dataLayerPush';
import { getItemDiscount } from '../gtm/helpers';
import type { EventContractMap } from '../types/analyticsEvents';

export const Analytics = {
    on<Event extends keyof EventContractMap>(action: Event, data: EventContractMap[Event]): void {
        dataLayerPush({ ...data, event: action });
    },
};

/**
 * Extracts the user ID from the Google Analytics device ID.
 * @example `GA1.1.xxxxxxxxxx.xxxxxxxxxx => xxxxxxxxxx-xxxxxxxxxx`
 * @link https://support.google.com/analytics/answer/11397207
 */
function extractGaUserIdFromCookie(gaCookie: string) {
    if (gaCookie) {
        // Remove the Google Analytics tracker from the device ID.
        const userIdParts = gaCookie.split('.').slice(-2);
        if (userIdParts.length === 2) {
            return userIdParts.join('-');
        }
    }
    return undefined;
}

/**
 * Returns the Google Analytics User ID from a browser cookie name.
 * @example `getGaUserId('_ga')`
 */
export function getGaUserId(cookieName: string) {
    const browserCookie = getCookieValue(cookieName);

    if (!browserCookie) {
        return undefined;
    }

    return extractGaUserIdFromCookie(browserCookie);
}

/**
 * Determines the fulfillment method for an item, handling scheduled autoship cases
 * If scheduledDeliveryDate has a value, returns "sth autoship scheduled"
 * Otherwise, returns the standard fulfillment method (e.g., "sth autoship", "sth one-time")
 */
export const getFulfillmentMethodForItem = (fulfillmentMethod: string | undefined, item: ItemUI): string | null => {
    if (!fulfillmentMethod) return null;

    const methodBase = fulfillmentMethod.toLowerCase().trim();
    const hasAutoship = Boolean(item.autoshipFrequency);
    const hasScheduledDate = Boolean(item.scheduledDeliveryDate);

    // If scheduledDeliveryDate has a value, return "sth autoship scheduled"
    if (hasScheduledDate) {
        return 'sth autoship scheduled';
    }

    // Default behavior: determine autoship status
    const autoshipStatus = hasAutoship ? 'autoship' : 'one-time';

    // If method is already "sth autoship" (combined), return as is
    if (methodBase === 'sth autoship' || (methodBase === DeliveryType.STH && hasAutoship)) {
        return hasAutoship ? 'sth autoship' : 'sth one-time';
    }

    return methodBase === DeliveryType.AUTOSHIP ? 'sth autoship' : `${methodBase} ${autoshipStatus}`;
};

/**
 * Gets all unique fulfillment methods for a group based on its items
 * This handles cases where a group contains items with different autoship statuses
 */
const getFulfillmentMethodsForGroup = (group: FulfillmentGroupsItemUI): string[] => {
    const methods = new Set<string>();

    for (const item of group.items) {
        const method = getFulfillmentMethodForItem(group.fulfillmentMethod, item);
        if (method) {
            methods.add(method);
        }
    }

    return Array.from(methods);
};

export const getFulfillmentMethodsString = (fulfillmentGroups: FulfillmentGroupsItemUI[]) => {
    const methods: string[] = [];

    for (const group of fulfillmentGroups) {
        const groupMethods = getFulfillmentMethodsForGroup(group);
        for (const method of groupMethods) {
            if (!methods.includes(method)) {
                methods.push(method);
            }
        }
    }

    return methods.join(' | ');
};

export const getPaymentMethodsString = (paymentMethods: PaymentMethod[]) => {
    return paymentMethods
        .map(method => {
            if (method.method === PaymentMethodsType.CREDITCARD && method.paymentCardType) {
                return method.paymentCardType;
            }
            return method.method.toLowerCase();
        })
        .join(',');
};

export const getDiscountCodesString = (discountCodes: DiscountCodes[]) => {
    return discountCodes.map(code => code.code).join(',');
};

export const getTotalDiscountValue = (fulfillmentGroups: FulfillmentGroupsItemUI[]) => {
    const getGroupDiscount = (group: FulfillmentGroupsItemUI) =>
        group.items.reduce((acc, item) => acc + getItemDiscount(item), 0);

    return fulfillmentGroups.reduce((acc, group) => acc + getGroupDiscount(group), 0);
};
